import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import { useCrearProveedor, useProveedores } from '../api/queries-finanzas';
import { FinanceDemoNotice } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { PlusIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { normalizarRif, RIF_MENSAJE, rifValido } from '../lib/rif';
import { formatMoney } from '../lib/format';
import { ResumenProveedorModal } from '../components/ResumenTercero';
import { SortableTh } from '../components/SortableTh';
import { useTableSort } from '../lib/useTableSort';

export function ProveedoresPage() {
  const { user, demo } = useAuth();
  const { usdRate } = useCurrency();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [proveedorResumenId, setProveedorResumenId] = useState<string | null>(null);
  const proveedores = useProveedores(search);
  const crear = useCrearProveedor();
  const canCreate = !demo && user !== null && ['admin', 'supervisor'].includes(user.rol);
  const rows = proveedores.data?.data ?? [];
  const { rows: sortedRows, sort, toggleSort } = useTableSort<
    (typeof rows)[number],
    'proveedor' | 'contacto' | 'condicion' | 'limite' | 'estado'
  >(
    rows,
    {
      proveedor: (row) => row.nombre,
      contacto: (row) => row.telefono ?? row.email,
      condicion: (row) => row.diasCredito,
      limite: (row) => row.limiteCreditoUsd,
      estado: (row) => row.activo,
    },
    'proveedor',
    'asc',
  );

  const [form, setForm] = useState({
    rif: '',
    nombre: '',
    email: '',
    telefono: '',
    direccion: '',
    diasCredito: '30',
    limiteCreditoUsd: '50000',
    islrPct: '0',
  });

  const submit = async () => {
    const rif = normalizarRif(form.rif);
    if (!rif || !form.nombre.trim()) {
      notify('RIF y nombre son obligatorios.', 'warning');
      return;
    }
    if (!rifValido(rif)) {
      notify(RIF_MENSAJE, 'warning');
      return;
    }
    try {
      await crear.mutateAsync({
        rif,
        nombre: form.nombre.trim(),
        ...(form.email ? { email: form.email } : {}),
        ...(form.telefono ? { telefono: form.telefono } : {}),
        ...(form.direccion ? { direccion: form.direccion } : {}),
        diasCredito: Number(form.diasCredito) || 0,
        limiteCreditoUsd: (Number(form.limiteCreditoUsd) || 0) / usdRate,
        islrPct: Number(form.islrPct) || 0,
      });
      notify(`Proveedor ${form.nombre.trim()} creado.`, 'success');
      setOpen(false);
      setForm({ rif: '', nombre: '', email: '', telefono: '', direccion: '', diasCredito: '30', limiteCreditoUsd: '50000', islrPct: '0' });
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear el proveedor.', 'error');
    }
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Compras y proveedores</p>
          <h1 className="page-title">Proveedores</h1>
          <p className="page-sub">Directorio de proveedores, condiciones de crédito y RIF.</p>
        </div>
        <button type="button" className="btn btn--primary" disabled={!canCreate} onClick={() => setOpen(true)}>
          <PlusIcon size={17} /> Nuevo proveedor
        </button>
      </header>

      <FinanceDemoNotice demo={demo} />

      <section className="card clients-card">
        <div className="clients-toolbar">
          <div className="search-input">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nombre o RIF…"
              aria-label="Buscar proveedores"
            />
          </div>
          <span className="pill pill--soft">{rows.length} proveedores</span>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Proveedor / RIF" sortKey="proveedor" sort={sort} onSort={toggleSort} />
                <SortableTh label="Contacto" sortKey="contacto" sort={sort} onSort={toggleSort} />
                <SortableTh label="Condición" sortKey="condicion" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="Límite crédito" sortKey="limite" sort={sort} onSort={toggleSort} />
                <SortableTh label="Estado" sortKey="estado" sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {proveedores.isLoading ? <tr><td colSpan={5}><div className="table-empty">Cargando proveedores…</div></td></tr> : null}
              {!proveedores.isLoading && rows.length === 0 ? <tr><td colSpan={5}><div className="table-empty">Sin proveedores registrados.</div></td></tr> : null}
              {sortedRows.map((row) => (
                <tr key={row.id} className="row-clickable" onClick={() => setProveedorResumenId(row.id)}>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.nombre}</strong>
                      <span>{row.rif}</span>
                    </div>
                  </td>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.telefono ?? 'Sin teléfono'}</strong>
                      <span>{row.email ?? row.direccion ?? 'Sin contacto'}</span>
                    </div>
                  </td>
                  <td>{row.diasCredito} días</td>
                  <td className="align-right">{formatMoney(row.limiteCreditoUsd, 'USD')}</td>
                  <td><span className={`status-badge status-badge--${row.activo ? 'success' : 'neutral'}`}>{row.activo ? 'Activo' : 'Inactivo'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {open ? (
        <Modal
          title="Nuevo proveedor"
          eyebrow="Directorio"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button type="button" className="btn btn--outline" onClick={() => setOpen(false)}>Cancelar</button>
              <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={crear.isPending}>
                {crear.isPending ? 'Guardando…' : 'Crear proveedor'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            <div className="form-grid">
              <label className="field">
                <span className="field-label">RIF *</span>
                <input
                  className="input"
                  value={form.rif}
                  onChange={(event) => setForm({ ...form, rif: event.target.value })}
                  onBlur={(event) => setForm({ ...form, rif: normalizarRif(event.target.value) })}
                  placeholder="J-30011111-1"
                  maxLength={12}
                />
                <span className="field-help">Formato J-12345678-9</span>
              </label>
              <label className="field">
                <span className="field-label">Nombre *</span>
                <input className="input" value={form.nombre} onChange={(event) => setForm({ ...form, nombre: event.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Teléfono</span>
                <input className="input" value={form.telefono} onChange={(event) => setForm({ ...form, telefono: event.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Correo</span>
                <input className="input" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
              </label>
              <label className="field field--wide">
                <span className="field-label">Dirección</span>
                <input className="input" value={form.direccion} onChange={(event) => setForm({ ...form, direccion: event.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Días de crédito</span>
                <input className="input" type="number" min="0" max="180" value={form.diasCredito} onChange={(event) => setForm({ ...form, diasCredito: event.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Límite de crédito Bs.</span>
                <input className="input" type="number" min="0" value={form.limiteCreditoUsd} onChange={(event) => setForm({ ...form, limiteCreditoUsd: event.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">ISLR por defecto (%)</span>
                <input className="input" type="number" min="0" max="100" step="0.01" value={form.islrPct} onChange={(event) => setForm({ ...form, islrPct: event.target.value })} />
              </label>
            </div>
          </div>
        </Modal>
      ) : null}

      {proveedorResumenId ? (
        <ResumenProveedorModal proveedorId={proveedorResumenId} onClose={() => setProveedorResumenId(null)} />
      ) : null}
    </div>
  );
}
